/**
 * Tiny UI sounds for the AI tutor, synthesised with the Web Audio API — no audio files to load and no
 * licences. Quiet by design; the student can turn them off (remembered in localStorage). Browsers only
 * let audio start after a user gesture, which every sound here follows (sending, tapping the mascot).
 */
const STORAGE_KEY = "examora.tutorSound";
let context: AudioContext | null = null;

export function isTutorSoundOn(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setTutorSoundOn(on: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {
    // storage blocked — the toggle still works for this page view
  }
}

function audio(): AudioContext | null {
  if (typeof window === "undefined" || !isTutorSoundOn()) return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  context ??= new Ctor();
  if (context.state === "suspended") void context.resume();
  return context;
}

/** One soft note: a sine (or triangle) with a quick attack and a smooth fade, optionally gliding in pitch. */
function note(ctx: AudioContext, { from, to = from, start = 0, length = 0.12, volume = 0.12, type = "sine" as OscillatorType }: { from: number; to?: number; start?: number; length?: number; volume?: number; type?: OscillatorType }) {
  const t0 = ctx.currentTime + start;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t0);
  osc.frequency.exponentialRampToValueAtTime(to, t0 + length);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(volume, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + length);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + length + 0.02);
}

/** Message sent: a short rising "pop". */
export function playSend() {
  const ctx = audio();
  if (ctx) note(ctx, { from: 420, to: 760, length: 0.09, volume: 0.1 });
}

/** Answer arrived: a light two-note chime. */
export function playReply() {
  const ctx = audio();
  if (!ctx) return;
  note(ctx, { from: 784, length: 0.16, volume: 0.09, type: "triangle" });
  note(ctx, { from: 1175, start: 0.09, length: 0.22, volume: 0.08, type: "triangle" });
}

/** The mascot was tapped: a bouncy "boop", a little different every time. */
export function playPoke() {
  const ctx = audio();
  if (!ctx) return;
  const base = 520 + Math.random() * 180;
  note(ctx, { from: base, to: base * 1.6, length: 0.08, volume: 0.11 });
  note(ctx, { from: base * 1.5, to: base * 1.15, start: 0.07, length: 0.1, volume: 0.08 });
}

/** Something went wrong: a soft falling tone. */
export function playSad() {
  const ctx = audio();
  if (ctx) note(ctx, { from: 520, to: 300, length: 0.35, volume: 0.08, type: "triangle" });
}
